<template>
    <!-- Loading state. -->
    <div v-if="loading" class="rounded-lg bg-gray-50 border border-gray-200 p-16 flex justify-center items-center text-gray-500 space-x-3 italic">
        <svg class="animate-spin text-gray-400 w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p>Loading...</p>
    </div>

    <!-- Empty state. -->
    <div v-else-if="materials.length === 0" class="rounded-lg bg-gray-50 border border-gray-200 p-16 text-gray-500 italic text-center">
        {{ emptyStateMessage }}
    </div>

    <div v-else>

      <template v-if="!isItem">
        <ui-toggle class="mb-4" label="Show Total Only" text="Show only a condensed total of all the materials."
                   name="total_only" v-model="totalOnly"></ui-toggle>
      </template>

      <template v-if="isEntireEstimate">
        <ui-alert-strip class="mb-8" colour="yellow"
                        message="You are viewing an entire estimate's materials. Optional sections are omitted from this view. You can still view an optional section's materials directly."></ui-alert-strip>
      </template>


      <template v-for="section in materials_structure.sections">
        <template v-for="task in section.tasks">

          <div v-if="showTask(task, section)">
              <h2 class="text-lg leading-6 font-medium text-gray-900 truncate mb-4"><strong class="font-bold">{{ section.title }}:</strong> {{ task.title }}</h2>
            </div>

            <template v-for="item in task.items">
              <div v-if="showItem(item, task, section)">
                <h3 class="mb-1 text-base leading-6 font-medium text-gray-900 truncate">{{ item.title }}</h3>
                <estimates-old-page-material-list-table class="mb-6" :updated="updated" :materials="relevantMaterials(item.estimate_material_ids, item.relevant_item_ids)"></estimates-old-page-material-list-table>
              </div>
            </template>

            <div v-if="showTask(task, section)">
              <h2 class="text-lg leading-6 font-bold text-gray-900 truncate mb-1">Total Task Materials</h2>
              <estimates-old-page-material-list-table class="mb-4" :updated="updated" :materials="relevantMaterials(task.estimate_material_ids, task.relevant_item_ids)"></estimates-old-page-material-list-table>
            </div>

          </template>

          <div v-if="showSection(section)">
            <h1 class="text-lg leading-6 font-bold text-gray-900 truncate mb-1">Total Section Materials</h1>
            <estimates-old-page-material-list-table class="mb-4" :updated="updated" :materials="relevantMaterials(section.estimate_material_ids, section.relevant_item_ids)"></estimates-old-page-material-list-table>
          </div>

        </template>


      <template v-if="isEntireEstimate">
        <h1 class="mb-1 text-lg leading-6 font-black text-gray-900 truncate">Total Estimate Materials</h1>
        <estimates-old-page-material-list-table :updated="updated" :materials="relevantMaterials(materials_structure.estimate_material_ids, materials_structure.relevant_item_ids)"></estimates-old-page-material-list-table>
      </template>

    </div>
</template>

<script>
import {chain, clone, sumBy} from 'lodash'
import {estimateStore} from '@stores'

export default {
  props: {
    contextType: String, //  section | task | item
    context: Object,
  },
  data() {
    return {
      totalOnly: true,
      materials: [],
      materials_structure: {},
      loading: false,
    }
    },
    computed: {
        $store () {
            return estimateStore
        },
        estimate () {
            return estimateStore.state.estimate
        },
        isSection(){
          return this.contextType === 'section'
        },
        isTask(){
          return this.contextType === 'task'
        },
        isItem(){
          return this.contextType === 'item'

        },
        isEntireEstimate(){
          return !(this.isSection || this.isTask || this.isItem)
        },
        emptyStateMessage () {
            if (this.isSection) return 'This section has no material.'
            if (this.isTask) return 'This task has no material.'
            if (this.isItem) return 'This item has no material.'
            return 'This estimate has no material.'
        },
    },
    methods: {
        showItem(item){
          // Don't bother checking other conditions if we only want a total only and if the thing we're
          // looking at is the item we've requested directly
          if (this.totalOnly) {
            if (this.isItem && this.context.id === item.id) {
              return true
            }else{
              return false
            }
          }

          if(this.isEntireEstimate) return true

          // We're viewing a section, does this item have a section ID that matches our context?
          if(this.isSection && this.context.id === item.section_id) return true

          // We're viewing a task, does this item have a task ID that matches our context?
          if(this.isTask && this.context.id === item.task_id) return true

          // We're viewing an item, but does it match the item we're asking to check against?
          if(this.isItem && this.context.id === item.id) return true

          return false;
        },
        showTask(task) {
          // Don't bother checking other conditions if we only want a total only and if the thing we're
          // looking at is the task we've requested directly
          if (this.totalOnly) {
            if (this.isTask && this.context.id === task.id) {
              return true
            }else{
              return false
            }
          }

          if (this.isEntireEstimate) return true

          // We're viewing an item, so don't show a task breakdown
          if (this.isItem) return false

          // We're viewing a section, does this task have a section ID that matches our context?
          if (this.isSection && this.context.id === task.section_id) return true

          // We're viewing a task, does this item have a task ID that matches our context?
          if(this.isTask && this.context.id === task.id) return true

          return false;
        },
        showSection(section){
          // Don't bother checking other conditions if we only want a total only and if the thing we're
          // looking at is the item we've requested directly
          if (this.totalOnly) {
            if (this.isSection && this.context.id === section.id) {
              return true
            }else{
              return false
            }
          }

          if (this.isEntireEstimate) return true

          // We're viewing an item or a task, so don't show a task breakdown
          if (this.isItem || this.isTask) return false

          // We're viewing a section, does this task have a section ID that matches our context?
          if (this.isSection && this.context.id === section.id) return true

          return false;
        },
      relevantMaterials(materialIds, relevantItemIds = []) {
        return chain(this.materials).keyBy('id').at(materialIds).filter((material) => {
          return _.isObject(material)
        }).map((material) => {
          let modifiedMaterial = clone(material)

          // We are looking at a list of materials within a section, task or item. Use a calculated quantity amount.
          let relevantItemRecords = chain(modifiedMaterial.item_usages).filter((usage) => {
            return relevantItemIds.includes(usage.id)
          }).value()

          modifiedMaterial.quantity = sumBy(relevantItemRecords, 'quantity')
          modifiedMaterial.price = sumBy(relevantItemRecords, 'cost')

          return modifiedMaterial
        }).value()
      },
      async fetch() {
        try {
          this.loading = true
          let contextType = this.contextType ?? null
          let contextId = this.context?.id

          let [materials, structure] = await Promise.all([
            this.estimate.listMaterials(contextType, contextId),
            this.estimate.listMaterialsStructure(contextType, contextId)
          ])

          this.materials = materials
          this.materials_structure = structure
        } finally {
          this.loading = false
        }
      },
      updated(materials) {
        this.fetch()
      },
    },
    mounted () {
        this.fetch()
    },
}
</script>
